import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IpostingItem } from '@typings/posting/posting.interface';
import { fuego } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { MAIN_HOME_SEC_TYPE } from '@typings/setting/MainHomeSecType';
import NoticeBox from './NoticeBox';
import { MainHomeBox } from '@pages/MainHome/common/commonStyle';
import {
  POSTING_NOTICE_TYPE,
  POSTING_NOTICE_TYPE_VALUE,
} from '@typings/mainHome/postingNotice';
import DocItem from '@template/CurrentDocs/DocItem';
import NoticeIconBox from './NoticeIconBox';
import { mainHomeSettingState } from '@atom/setting/homeSettingState';
import { useRecoilValue } from 'recoil';

export const Wrapper = styled.div`
  grid-area: ${MAIN_HOME_SEC_TYPE.POSTINGNOTICE};
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
`;

const EmptyPage = styled.div`
  width: 100%;
  color: var(--subTextColor);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
`;

const Component = styled(MainHomeBox)`
  padding: 0;
`;

type Props = {
  noShowDate?: boolean;
  type: POSTING_NOTICE_TYPE_VALUE;
  maxHeight?: string;
};
function CumuNotice({ noShowDate, type, maxHeight }: Props) {
  const history = useNavigate();
  const [allNotices, setAllNotices] = useState<IpostingItem[]>([]);
  const mainHomeSet = useRecoilValue(mainHomeSettingState);

  // 🆕 선택된 게시판에서 공지 가져오기
  const selectedPaths = (mainHomeSet as any)?.noticeIntegrate?.length > 0
    ? (mainHomeSet as any).noticeIntegrate
    : [CollectionPath.POSTING];

  // 동적으로 선택된 게시판들의 공지 가져오기
  useEffect(() => {
    if (!selectedPaths || selectedPaths.length === 0) {
      setAllNotices([]);
      return;
    }

    const fetchNotices = async () => {
      const noticePromises = selectedPaths.map(async (path) => {
        const snapshot = await fuego.db
          .collection(path)
          .where('isNotice', '==', true)
          .orderBy('createdAt', 'desc')
          .limit(10)
          .get();

        return snapshot.docs.map((doc) => ({
          id: doc.id,
          collectionPath: path,
          ...doc.data(),
        })) as IpostingItem[];
      });

      const results = await Promise.all(noticePromises);
      const merged = results.flat() as IpostingItem[];

      // createdAt 기준 정렬
      merged.sort((a, b) => {
        const aTime = (a.createdAt as any)?.seconds || a.createdAt || 0;
        const bTime = (b.createdAt as any)?.seconds || b.createdAt || 0;
        return bTime - aTime;
      });

      setAllNotices(merged);
    };

    fetchNotices();
  }, [selectedPaths]);

  const handleItemClick = useCallback(
    (collectionPath, id) => {
      history(`/${collectionPath}/${id}`);
    },
    [history],
  );

  // 🔧 공지 글 체크
  if (allNotices.length === 0)
    return (
      <EmptyPage>
        <p>공지 글이 없습니다.</p>
      </EmptyPage>
    );

  const drawMap = {
    [POSTING_NOTICE_TYPE.ICONIMAGE]: (item, path: string) => (
      <NoticeIconBox
        key={item.id}
        data={item}
        handleClick={(_, id) => handleItemClick(path, id)}
        iconImg={mainHomeSet?.postingNoticeIconImage || ''}
      />
    ),
    [POSTING_NOTICE_TYPE.DEFAULT]: (item, path: string) => (
      <Component key={item.id} onClick={() => handleItemClick(path, item.id)}>
        <DocItem icon={'default'} data={item} noShowDate={noShowDate} />
      </Component>
    ),
    [POSTING_NOTICE_TYPE.CARD]: (item, path: string) => (
      <NoticeBox
        key={item.id}
        data={item}
        handleClick={(_, id) => handleItemClick(path, id)}
      />
    ),
  } as const;

  return (
    <>
      <Wrapper style={{ maxHeight }}>
        {allNotices.map((item) =>
          drawMap[type]?.(
            item,
            (item as any).collectionPath || CollectionPath.POSTING,
          ),
        )}
      </Wrapper>
    </>
  );
}

export default CumuNotice;
